import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Menu, Avatar, Divider, Button, Loader } from '@mantine/core';
import {
  IconBuildingWarehouse,
  IconCash,
  IconCashRegister,
  IconCreditCard,
  IconUser,
  IconSettings,
  IconDeviceDesktop,
  IconWorld,
  IconLogout,
  IconCheck,
  IconLock,
  IconLayoutKanban
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changeLanguage, getCurrentLanguage } from '../../../utils/i18n';
import { clearUserAction, switchUserAction } from '../../../redux/actions';
import { useGetStoreById, useGetStores } from '../../../hooks/stores/queries';
import { useGetCashierboxes } from '../../../hooks/cashierboxes/queries';
import { IStore } from '../../../interfaces/Store';
import cookies from 'js-cookie';
import { usePostHog } from 'posthog-js/react';
import { trackEvent } from '../../../utils/monitoring/posthog';
import { MonitoringEventTypes } from '../../../utils/monitoring/Monitoring_Events_Enum';
import { useRequestPinLogin } from '../../../hooks/useRequestPinLogin';
import useCanUseCFDIInvoicing from '../../../hooks/useCanUseCFDIInvoicing';
import { CompanyPlanLevel } from '../../../enum/CompanyPlanLevelEnum';
import { useRequestCashierPinValidation } from '../../../hooks/useRequestCashierPinValidation';
import { openCashDrawer } from '../../../utils/cashDrawer';
import { showNotification } from '../../../utils/Notification/Notification';
import CashierBoxMovementDrawer from '../../../pages/CashierBoxes/CashierBoxMovementDrawer';
import { replaceSearchParamIfChanged } from '../../../utils/navigation/replaceSearchParamIfChanged';
import { hasPermission } from '../../../utils/permission-utils';
import { CustomerDisplayModal } from '../../../customerDisplay/CustomerDisplayModal';

const ALL_STORES_VALUE = 'all';

const getStoreIdFromUser = (user: any) => {
  if (typeof user?.store === 'string') return user.store;
  return user?.store?._id || '';
};

export function StoreUserMenu() {
  const posthog = usePostHog();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId: string | null = searchParams.get('storeId');
  const isRoutesPageAllStoresFilter =
    location.pathname === '/routes' && searchParamStoreId === ALL_STORES_VALUE;
  const [activeStore, setActiveStore] = useState<Partial<IStore> | null>({
    _id: '',
    name: 'Cargando...'
  });
  const { user, company } = useSelector((state: any) => state.user);
  const { name, isCompanyAdmin, _id } = user;
  const canMutateCashierBoxes = hasPermission(user, 'cashierBoxes', 'canMutate');
  const canSelectOrdersHistoryAllStores =
    location.pathname === '/reports/orders-history-report' &&
    Boolean(user.permission?.allowMultiStoreView?.canView);
  const isOrdersHistoryPageAllStoresFilter =
    canSelectOrdersHistoryAllStores && searchParamStoreId === ALL_STORES_VALUE;
  const isAllStoresFilter =
    isRoutesPageAllStoresFilter || isOrdersHistoryPageAllStoresFilter;
  const { canUseCFDIInvoicing } = useCanUseCFDIInvoicing(
    isAllStoresFilter ? '' : searchParamStoreId || ''
  );
  const history = useHistory();
  const { t } = useTranslation(['CommonButtons', 'Notifications', 'RoutesPage', 'CustomerDisplay', 'WorkBoard']);
  const [currLang, setCurrLang] = useState(getCurrentLanguage());
  const [displayOpened, setDisplayOpened] = useState(false);
  const [isManualDrawerLoading, setIsManualDrawerLoading] = useState(false);
  const dispatch = useDispatch();
  const onLogout = () => {
    dispatch(clearUserAction());
    window.location.href = '/';
  };
  const { requestPinLogin } = useRequestPinLogin();
  const { requestPin } = useRequestCashierPinValidation();
  const [
    cashierBoxMovementDrawerOpened,
    {
      open: openCashierBoxMovementDrawer,
      close: closeCashierBoxMovementDrawer
    }
  ] = useDisclosure(false);
  const sessionQueryClient = useQueryClient();
  const onPinLogin = async () => {
    try {
      const res = await requestPinLogin();
      if (res) {
        await sessionQueryClient.cancelQueries();
        sessionQueryClient.clear();
        dispatch(switchUserAction(res));
        setTimeout(() => {
          history.push('/'); // PUSH TO HOME PAGE SINCE ALL USERS CAN SEE THIS PAGE
          window.location.reload();
        }, 1000);
      }
    } catch (e) {
      console.log('error', e);
    }
  };
  const { isLoading: isGetStoresLoading, data: stores = [] } = useGetStores(true);
  const userStoreId = getStoreIdFromUser(user);
  const selectedStoreId =
    activeStore?._id === ALL_STORES_VALUE || isAllStoresFilter
      ? ''
      : activeStore?._id || searchParamStoreId || userStoreId || '';
  const {
    data: selectedStore,
    isLoading: isGetSelectedStoreLoading,
    isError: isGetSelectedStoreError,
  } = useGetStoreById(!!selectedStoreId, selectedStoreId || '');
  const {
    data: cashierBoxes = [],
    isLoading: isGetCashierBoxesLoading,
    isError: isGetCashierBoxesError
  } = useGetCashierboxes(canMutateCashierBoxes && !!selectedStoreId, selectedStoreId || '');
  const extraMenuItems = [];

  //SHOW SUSCRIPTION SESSION IN CASE IS SUSCRIBED TO A PLAN OR CFDI SERVICE
  if (isCompanyAdmin) {
    extraMenuItems.push(
      <Menu.Item
        key={'/settings/store-details' + '-userMenuButton'}
        leftSection={<IconSettings size={16} />}
        onClick={() => {
          history.push({
            pathname: '/settings/store-details',
            search: `?storeId=${searchParamStoreId}`
          });
        }}>
        {t('CommonButtons:settings')}
      </Menu.Item>
    );
    // WE SHOW THIS MENU BECAUSE IT COULD HAVE CFDI INVOICE SUB WITHOUT PLAN SUB
    if (company?.planLevel !== CompanyPlanLevel.TRIAL || canUseCFDIInvoicing) {
      extraMenuItems.push(
        <Menu.Item
          key={'/settings/subscription' + '-userMenuButton'}
          component="a"
          leftSection={<IconCreditCard size={16} />}
          onClick={() => {
            history.push({
              pathname: '/settings/subscription',
              search: `?storeId=${searchParamStoreId}`
            });
          }}>
          {t('CommonButtons:subscription')}
        </Menu.Item>
      );
    }
  }

  const replaceSelectedStoreInUrl = (storeId: string | null) => {
    replaceSearchParamIfChanged({
      history,
      pathname: location.pathname,
      search: location.search,
      key: 'storeId',
      value: storeId || ''
      // state: { storeID: storeId } this should fix of store on catalog pages
    });
  };

  const onChangeSelectedStore = (storeId: string | null, store: Partial<IStore>) => {
    cookies.set('currencySymbol', store.currencySymbol || '$'); //USED FOR formatMoneyString
    replaceSelectedStoreInUrl(storeId);
  };

  const onStoreSelected = (store: Partial<IStore>) => {
    replaceSelectedStoreInUrl(store._id || null);
    setActiveStore({
      _id: store._id,
      name: store.name,
      currencySymbol: store.currencySymbol
    });
    trackEvent(posthog, MonitoringEventTypes.STORE_CHANGED_FROM_APP_NAV_HEADER, {
      storeId: store._id,
      userId: _id
    });
  };

  const onAllStoresSelected = () => {
    const allStores = {
      _id: ALL_STORES_VALUE,
      name: t('RoutesPage:allStores'),
      currencySymbol: activeStore?.currencySymbol || cookies.get('currencySymbol') || '$'
    };
    onStoreSelected(allStores);
    cookies.set('currencySymbol', allStores.currencySymbol);
  };

  useEffect(() => {
    if (!stores) return;

    if (isAllStoresFilter) {
      if (activeStore?._id !== ALL_STORES_VALUE) {
        setActiveStore({
          _id: ALL_STORES_VALUE,
          name: t('RoutesPage:allStores'),
          currencySymbol: '$'
        });
      }
      return;
    }

    const initialStoreId = searchParamStoreId || userStoreId;
    const selectedStore = stores.find((store) => store?._id === initialStoreId) || stores[0];
    if (selectedStore?._id === activeStore?._id) return;

    setActiveStore({
      _id: selectedStore?._id || '',
      name: selectedStore?.name || 'No pudimos cargar la sucursal',
      currencySymbol: selectedStore?.currencySymbol || '$'
    });
  }, [stores, searchParamStoreId, userStoreId, activeStore?._id, isAllStoresFilter, t]);

  useEffect(() => {
    if (activeStore?._id && activeStore._id !== ALL_STORES_VALUE) {
      onChangeSelectedStore(activeStore?._id, activeStore);
    }
  }, [activeStore]);

  const onOpenCashDrawer = async () => {
    if (isManualDrawerLoading) return;

    if (!selectedStoreId || isGetSelectedStoreError || !selectedStore) {
      showNotification({
        type: 'Error',
        title: t('Notifications:cashDrawerOpenFailedTitle'),
        message: t('Notifications:cashDrawerConfigUnavailableBody')
      });
      return;
    }

    setIsManualDrawerLoading(true);

    try {
      if (selectedStore.cashDrawerConfig?.requirePinForManualOpen) {
        const validatedUser = await requestPin({
          showSuccessNotification: false
        });

        if (!validatedUser?._id) return;
      }

      const result = await openCashDrawer({ source: 'manual' });

      if (!result.success) {
        showNotification({
          type: 'Error',
          title: t('Notifications:cashDrawerOpenFailedTitle'),
          message: result.errorMessage || t('Notifications:cashDrawerOpenFailedBody')
        });
        return;
      }

      showNotification({
        type: 'Success',
        title: t('Notifications:cashDrawerOpenSuccessTitle')
      });
    } catch (error) {
      showNotification({
        type: 'Error',
        title: t('Notifications:cashDrawerOpenFailedTitle'),
        message: t('Notifications:cashDrawerOpenFailedBody')
      });
    } finally {
      setIsManualDrawerLoading(false);
    }
  };

  const onAddCashierBoxMovement = () => {
    if (!canMutateCashierBoxes) return;

    if (!selectedStoreId) {
      showNotification({
        type: 'Error',
        title: t('Notifications:error'),
        message: t('Notifications:cashierBoxMovementStoreRequiredBody')
      });
      return;
    }

    if (isGetCashierBoxesLoading) return;

    if (isGetCashierBoxesError) {
      showNotification({
        type: 'Error',
        title: t('Notifications:error'),
        message: t('Notifications:cashierBoxMovementBoxesLoadFailedBody')
      });
      return;
    }

    if (!cashierBoxes.length) {
      showNotification({
        type: 'Error',
        title: t('Notifications:error'),
        message: t('Notifications:cashierBoxMovementNoBoxesBody')
      });
      return;
    }

    openCashierBoxMovementDrawer();
  };

  return (
    <>
      <Menu width={260} withArrow id="store-user-menu" middlewares={{ size: { padding: 8 } }}>
        <Menu.Target>
          <Button
            variant="default"
            style={{ width: '260px', maxWidth: '260px' }}
            mr={10}
            rightSection={
              <Avatar size={'sm'} radius="xl" color="#003684">
                {name?.substring(0, 2).toUpperCase()}
              </Avatar>
            }>
            {activeStore?.name}
          </Button>
        </Menu.Target>

        <Menu.Dropdown style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
          <Menu.Label style={{ display: 'flex', alignItems: 'center' }}>
            <IconBuildingWarehouse size={16} style={{ marginRight: 8 }} />
            {t('CommonButtons:stores')}
          </Menu.Label>
          {isGetStoresLoading ? (
            <Loader size={16} />
          ) : (
            <>
              {canSelectOrdersHistoryAllStores && (
                <Menu.Item
                  onClick={onAllStoresSelected}
                  leftSection={
                    activeStore?._id === ALL_STORES_VALUE ? <IconCheck size={16} /> : null
                  }>
                  {t('RoutesPage:allStores')}
                </Menu.Item>
              )}
              {stores?.map((b) => (
                <Menu.Item
                  key={b._id}
                  onClick={() => onStoreSelected(b)}
                  leftSection={activeStore?._id === b._id ? <IconCheck size={16} /> : null}>
                  {b.name}
                </Menu.Item>
              ))}
            </>
          )}

          <Divider my="sm" />

          {canMutateCashierBoxes ? (
            <Menu.Item
              leftSection={isGetCashierBoxesLoading ? <Loader size={16} /> : <IconCash size={16} />}
              disabled={isGetCashierBoxesLoading}
              onClick={onAddCashierBoxMovement}>
              {t('CommonButtons:addCashierBoxMovement')}
            </Menu.Item>
          ) : null}
          <Menu.Item
            leftSection={
              isManualDrawerLoading ? <Loader size={16} /> : <IconCashRegister size={16} />
            }
            disabled={!selectedStoreId || isGetSelectedStoreLoading || isManualDrawerLoading}
            onClick={onOpenCashDrawer}>
            {t('CommonButtons:openCashDrawer')}
          </Menu.Item>
          <Divider my="sm" />

          <Menu.Label>Cuenta</Menu.Label>
          {extraMenuItems}
          <Menu.Item
            leftSection={<IconUser size={16} />}
            onClick={() => {
              history.push('/profile');
            }}>
            {t('CommonButtons:profile')}
          </Menu.Item>
          <Divider my="sm" />
          <Menu.Item
            leftSection={<IconDeviceDesktop size={16} />}
            onClick={() => setDisplayOpened(true)}>
            {t('CustomerDisplay:title')}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconLayoutKanban size={16} />}
            onClick={() => history.push({
              pathname: '/work-board',
              search: selectedStoreId ? `?storeId=${encodeURIComponent(selectedStoreId)}` : ''
            })}>
            {t('WorkBoard:title')}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconWorld size={16} />}
            onClick={() => {
              const nextLang = currLang.includes('es') ? 'en' : 'es';
              changeLanguage(nextLang);
              setCurrLang(nextLang);
            }}>
            {t('CommonButtons:changeLanguage')}
          </Menu.Item>
          <Menu.Item color="blue" leftSection={<IconLock size={16} />} onClick={onPinLogin}>
            {t('CommonButtons:changeSessionByPin')}
          </Menu.Item>
          <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={onLogout}>
            {t('CommonButtons:logout')}
          </Menu.Item>
          </div>
        </Menu.Dropdown>
      </Menu>
      <CustomerDisplayModal opened={displayOpened} onClose={() => setDisplayOpened(false)} />
      {canMutateCashierBoxes ? (
        <CashierBoxMovementDrawer
          opened={cashierBoxMovementDrawerOpened}
          onClose={closeCashierBoxMovementDrawer}
          storeId={selectedStoreId}
          cashierBoxes={cashierBoxes}
        />
      ) : null}
    </>
  );
}
